import { normalizeMultiDimensionType } from "../workflowVariables.constants";

/**
 * Cell value for API: number columns → JSON number; date → `yyyy-mm-dd` string; text → string.
 * @author: Sejal
 */
export const coerceCellValueForApi = (raw, dimensionType) => {
  const t = normalizeMultiDimensionType(dimensionType);
  if (t === "number") {
    if (raw === "" || raw == null) return "";
    const n = typeof raw === "number" ? raw : Number(String(raw).trim());
    return Number.isFinite(n) ? n : "";
  }
  if (t === "date") {
    if (raw === "" || raw == null) return "";
    const s = typeof raw === "string" ? raw.trim() : String(raw);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  }
  return raw == null ? "" : String(raw);
};

/**
 * State for inputs: number cells as finite numbers; date as `yyyy-mm-dd`; text as string.
 * @author: Sejal
 */
export const coerceCellValueFromApi = (value, dimensionType) => {
  const t = normalizeMultiDimensionType(dimensionType);
  if (t === "number") {
    if (value === "" || value == null) return "";
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const n = Number(String(value).trim());
    return Number.isFinite(n) ? n : "";
  }
  if (t === "date") {
    if (value === "" || value == null) return "";
    const s = typeof value === "string" ? value.trim() : String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  }
  return value == null ? "" : String(value);
};

/**
 * Full workflow variable from API (`response.data`) → matrix state for the modal.
 * Expects `attributes.dimensions` and `attributes.options` in backend shape.
 * @author: Sejal
 */

export const mapDimensionsFromApi = (dimensions) => {
  if (!Array.isArray(dimensions)) return [];
  return dimensions.map((d) => ({
    id: d.id,
    name: (d.label ?? "").trim(),
    type: normalizeMultiDimensionType(d.type),
  }));
};

const dimensionTypeById = (apiDimensions) => {
  const map = {};
  for (const d of apiDimensions || []) {
    if (d != null && d.id != null) map[d.id] = normalizeMultiDimensionType(d.type);
  }
  return map;
};

export const mapOptionsFromApi = (options, apiDimensions = []) => {
  if (!Array.isArray(options)) return [];
  const types = dimensionTypeById(apiDimensions);

  return options.map((opt) => {
    const cells = {};
    for (const c of opt.cells || []) {
      if (c != null && c.dimensionId != null) {
        const dimType = types[c.dimensionId] ?? "text";
        cells[c.dimensionId] = coerceCellValueFromApi(c.value, dimType);
      }
    }
    return {
      id: opt.value,
      label: opt.label ?? "",
      cells,
    };
  });
};

/**
 * @param {object|undefined|null} variable - Full variable object (e.g. API `data` with `attributes`)
 * @returns {{ dimensions: object[], options: object[] }|null}
 */
export const getMultiDimensionMatrixFromVariable = (variable) => {
  const attrs = variable?.attributes;
  if (!attrs?.multiDimension) return null;

  const rawDims = attrs.dimensions || [];
  return {
    dimensions: mapDimensionsFromApi(rawDims),
    options: mapOptionsFromApi(attrs.options || [], rawDims),
  };
};
